import React from 'react';
import { motion } from 'framer-motion';
import { ANIMAL_DATA } from '../data/animals';

const Result = ({ animalKey }) => {
  // 데이터가 없을 경우를 대비한 기본값 설정
  const animal = ANIMAL_DATA[animalKey] || {
    name: "미지의 존재",
    desc: "당신의 본능은 아직 현대 과학으로 정의할 수 없는 영역에 있습니다.",
    advice: "조금 더 자신을 관찰하는 시간을 가져보세요.",
    tags: ["미스터리", "잠재력"],
    color: "#60A5FA",
    img: "/images/animals/default.jpg",
    title: "UNKNOWN SPIRIT"
  };

  const isMythical = animal.isLegendary;

  // [연출 2] 점진적 공개를 위한 애니메이션 설정
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.5
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
    show: { 
      opacity: 1, 
      y: 0, 
      filter: 'blur(0px)',
      transition: { duration: 0.8, ease: "easeOut" } 
    }
  };

  // 공유하기 함수
  const handleShare = async () => {
    const shareData = {
      title: '심연의 본능 테스트',
      text: `나의 잠재적 본능은 [${animal.name}]입니다! 당신의 본능도 확인해보세요.`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('링크가 클립보드에 복사되었습니다! 친구들에게 공유해보세요.');
      }
    } catch (err) {
      console.log('공유 실패:', err);
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#000D1A', minHeight: '100vh', display: 'flex', 
      flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', 
      padding: '60px 20px', color: 'white', position: 'relative', overflow: 'hidden'
    }}>
      
      {/* [연출 1] 배경 빛 가루 (Particle System) */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: '110vh', x: `${Math.random() * 100}%`, opacity: 0 }}
          animate={{ 
            y: '-10vh', 
            opacity: [0, 0.7, 0],
            scale: [1, 1.5, 1]
          }}
          transition={{ 
            duration: Math.random() * 4 + 4, 
            repeat: Infinity, 
            delay: Math.random() * 5,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            width: isMythical ? '3px' : '2px',
            height: isMythical ? '3px' : '2px',
            backgroundColor: isMythical ? '#FFD700' : animal.color,
            borderRadius: '50%',
            filter: 'blur(1px)',
            zIndex: 1,
            boxShadow: `0 0 10px ${isMythical ? '#FFD700' : animal.color}`
          }}
        />
      ))}

      {/* 메인 결과 영역 (Stagger Container) */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        style={{ width: '100%', maxWidth: '450px', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10 }}
      >
        
        {/* 1. 이미지 및 후광 섹션 */}
        <motion.div variants={item} style={{ position: 'relative', width: '100%', maxWidth: '320px', marginBottom: '40px' }}>
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
              rotate: [0, 90, 180, 270, 360]
            }}
            transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
            style={{
              position: 'absolute', top: '-10%', left: '-10%', right: '-10%', bottom: '-10%',
              background: `radial-gradient(circle, ${animal.color}44 0%, transparent 70%)`,
              filter: 'blur(40px)', zIndex: -1
            }}
          />

          <img 
            src={animal.img} 
            alt={animal.name} 
            style={{ 
              width: '100%', aspectRatio: '1 / 1', objectFit: 'cover',
              borderRadius: '40px', 
              border: `1px solid ${isMythical ? '#FFD700' : 'rgba(255,255,255,0.1)'}`,
              boxShadow: `0 30px 60px rgba(0,0,0,0.8), 0 0 20px ${animal.color}22`
            }} 
          />

          <motion.div 
            whileHover={{ scale: 1.1 }}
            style={{
              position: 'absolute', top: '-10px', right: '-10px',
              padding: '8px 18px', borderRadius: '20px', fontSize: '12px', fontWeight: '900',
              backgroundColor: isMythical ? '#FFD700' : '#1F2937',
              color: isMythical ? '#000' : '#FFF',
              boxShadow: '0 10px 20px rgba(0,0,0,0.5)',
              letterSpacing: '2px'
            }}
          >
            {isMythical ? 'LEGENDARY' : 'WILD'}
          </motion.div>
        </motion.div>

        {/* 2. 타이틀 & 이름 */}
        <motion.p variants={item} style={{ 
          color: isMythical ? '#FFD700' : animal.color, 
          letterSpacing: '5px', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px' 
        }}>
          {animal.title || (isMythical ? 'MYTHICAL SPIRIT' : 'WILD INSTINCT')}
        </motion.p>
        
        <motion.h1 variants={item} style={{ 
          fontSize: '3rem', marginBottom: '20px', fontWeight: '900', color: '#FFF',
          textShadow: isMythical ? `0 0 20px ${animal.color}44` : 'none'
        }}>
          {animal.name}
        </motion.h1>

        {/* 3. 태그 영역 */}
        <motion.div variants={item} style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '30px' }}>
          {animal.tags?.map((tag, i) => (
            <span key={i} style={{ 
              fontSize: '11px', padding: '6px 16px', borderRadius: '50px',
              backgroundColor: 'rgba(255,255,255,0.05)', color: '#9CA3AF',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              #{tag}
            </span>
          ))}
        </motion.div>

        {/* 4. 설명 & 조언 박스 */}
        <motion.div variants={item} style={{
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '28px', padding: '35px 25px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(15px)', textAlign: 'center', width: '100%'
        }}>
          <p style={{ lineHeight: '1.9', color: '#CBD5E1', fontSize: '1.05rem', marginBottom: '35px', wordBreak: 'keep-all' }}>
            {animal.desc}
          </p>

          <div style={{ 
            backgroundColor: 'rgba(0,0,0,0.4)', padding: '25px', borderRadius: '20px',
            borderLeft: `4px solid ${isMythical ? '#FFD700' : animal.color}`,
            textAlign: 'left'
          }}>
            <span style={{ fontSize: '11px', color: isMythical ? '#FFD700' : animal.color, fontWeight: 'bold', display: 'block', marginBottom: '10px', letterSpacing: '1px' }}>
              SPIRITUAL ADVICE
            </span>
            <p style={{ fontSize: '0.95rem', lineHeight: '1.7', color: '#94A3B8', margin: 0, fontStyle: 'italic' }}>
              "{animal.advice}"
            </p>
          </div>
        </motion.div>

        {/* 5. 하단 메뉴 섹션 */}
        <motion.div variants={item} style={{ marginTop: '50px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          <motion.button 
            whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255,255,255,0.2)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            style={{
              padding: '16px 45px', borderRadius: '50px', backgroundColor: '#FFF', color: '#000',
              fontWeight: '800', border: 'none', cursor: 'pointer', fontSize: '15px',
              letterSpacing: '1px'
            }}
          >
            새로운 운명 탐색하기
          </motion.button>
          
          <motion.span 
            whileHover={{ color: '#FFF', scale: 1.1 }}
            onClick={handleShare}
            style={{ 
              color: '#4B5563', fontSize: '13px', textDecoration: 'underline', 
              cursor: 'pointer', transition: '0.3s' 
            }}
          >
            결과 기록 공유하기 (Share)
          </motion.span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Result;